import { Link } from "react-router-dom";
import { ImageOff, ArrowRight, ShieldCheck } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../i18n/LanguageProvider";
import { useRegisterAlternates } from "../components/layout/Layout";
import { pagePath } from "../config/pages";
import { PageHero, CTABand } from "../components/Sections";
import { Reveal } from "../components/motion/Reveal";
import { webPageSchema, breadcrumbSchema } from "../lib/schema";

const META = {
  title: {
    en: "Before & After | Dental Treatment in Vietnam | Dentel Vietnam",
    fr: "Avant / Après | Soins dentaires au Vietnam | Dentel Vietnam",
    de: "Vorher / Nachher | Zahnbehandlung in Vietnam | Dentel Vietnam",
  },
  desc: {
    en: "Verified before-and-after cases will be published here with patient consent. We do not display unverified or misleading images.",
    fr: "Des cas avant/après vérifiés seront publiés ici avec le consentement des patients. Nous n’affichons pas d’images non vérifiées ou trompeuses.",
    de: "Verifizierte Vorher-Nachher-Fälle werden hier mit Einwilligung der Patienten veröffentlicht. Wir zeigen keine ungeprüften oder irreführenden Bilder.",
  },
  h1: { en: "Before & After", fr: "Avant / Après", de: "Vorher / Nachher" },
  intro: {
    en: "We will publish real before-and-after cases here only with patient consent and clear context. We do not use stock or misleading images to imply results.",
    fr: "Nous ne publierons ici de vrais cas avant/après qu’avec le consentement des patients et un contexte clair. Nous n’utilisons pas d’images génériques ou trompeuses.",
    de: "Wir veröffentlichen hier echte Vorher-Nachher-Fälle nur mit Einwilligung der Patienten und klarem Kontext. Wir verwenden keine Stock- oder irreführenden Bilder.",
  },
  emptyTitle: {
    en: "Verified cases coming soon",
    fr: "Cas vérifiés à venir",
    de: "Verifizierte Fälle in Kürze",
  },
};

export default function BeforeAfter() {
  const { t, lang, p } = useI18n();
  const alternates = { en: pagePath("beforeAfter", "en"), fr: pagePath("beforeAfter", "fr"), de: pagePath("beforeAfter", "de") };
  useRegisterAlternates(alternates);
  const path = pagePath("beforeAfter", lang);
  const title = META.h1[lang] || META.h1.en;

  return (
    <>
      <Seo
        title={META.title[lang] || META.title.en}
        description={META.desc[lang] || META.desc.en}
        path={path}
        alternates={alternates}
        lang={lang}
        jsonLd={[
          webPageSchema({ name: title, description: META.desc[lang] || META.desc.en, path }),
          breadcrumbSchema([{ name: t.common.home, path: p("home") }, { name: title, path }]),
        ]}
      />
      <PageHero eyebrow={t.nav.beforeAfter} title={title} intro={META.intro[lang] || META.intro.en} crumbs={[{ name: t.nav.beforeAfter, to: path }]} />

      <section className="container-dv py-16 lg:py-20">
        <Reveal>
          <div className="rounded-3xl border border-dashed border-navy/20 bg-white p-10 text-center shadow-soft" data-testid="beforeafter-empty">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-navy/5 text-navy/60">
              <ImageOff className="h-8 w-8" />
            </div>
            <h2 className="mt-6 text-2xl text-navy">{META.emptyTitle[lang] || META.emptyTitle.en}</h2>
            <p className="mx-auto mt-3 max-w-xl text-navy/65 leading-relaxed">{META.desc[lang] || META.desc.en}</p>
            <div className="mx-auto mt-6 inline-flex items-center gap-2 rounded-full bg-turquoise/10 px-4 py-2 text-sm font-semibold text-turquoise">
              <ShieldCheck className="h-4 w-4" /> {t.reviews.title}
            </div>
            <div>
              <Link to={p("contact")} data-testid="beforeafter-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-dental px-7 py-4 text-base font-semibold text-white hover:bg-dental-dark transition-colors">
                {t.cta.myFreePlan}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <CTABand />
    </>
  );
}
